import { Code, ConnectError } from "@connectrpc/connect";
import { ActionableErrorDetails } from "gitpod-next-api/gitpod/v1/actionable_error_pb";
import { PromptValidationErrorDetails } from "gitpod-next-api/gitpod/v1/agent_pb";
import { PolicyEnforcedFailedPreconditionDetails } from "gitpod-next-api/gitpod/v1/organization_pb";
import { z } from "zod";
import type { ActionableError } from "@/utils/environment-error-classifier";

/**
 * Formats policy-specific error messages for better user experience.
 * Uses the server-provided error description which includes the policy limit
 * and current count (e.g., "(3/3)").
 */
export function formatPolicyError(error: ConnectError): string | null {
    const details = error.findDetails(PolicyEnforcedFailedPreconditionDetails);
    if (details.length > 0) {
        // The server error description (rawMessage) already contains the
        // user-friendly message with limit info. Use it directly.
        return error.rawMessage;
    }
    return null;
}

export type PromptValidationWarning = {
    cleanedText: string;
    removedCount: number;
};

/**
 * Extracts prompt validation error details from a ConnectError.
 * Returns the cleaned text and removed count if the error is a prompt validation error, null otherwise.
 */
export function extractPromptValidationDetails(error: unknown): PromptValidationWarning | null {
    if (!(error instanceof ConnectError) || error.code !== Code.InvalidArgument) {
        return null;
    }
    const details = error.findDetails(PromptValidationErrorDetails);
    if (details.length === 0) {
        return null;
    }
    return {
        cleanedText: details[0].cleanedText,
        removedCount: details[0].removedCount,
    };
}

/**
 * A loose matcher for stripe errors
 */
const stripeLikeErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    type: z.string(),
});

/**
 * A loose matcher for stripe load errors
 */
const stripeLikeLoadErrorSchema = z.object({
    message: z.string(),
    status: z.number(),
    type: z.string(),
});

/**
 * Formats prompt validation error messages for better user experience.
 * Extracts Unicode code point and position from backend error message.
 * Backend format: "prompt_contains_unsafe_characters: U+XXXX (category) at position N"
 */
export function formatPromptValidationError(errorMessage: string): string | null {
    if (!errorMessage.includes("prompt_contains_unsafe_characters")) {
        return null;
    }

    // Extract Unicode code point (e.g., "U+200B") and position from the error message
    const codePointMatch = /U\+[0-9A-Fa-f]+/.exec(errorMessage);
    const positionMatch = /at position (\d+)/.exec(errorMessage);

    const codePoint = codePointMatch?.[0] ?? "unknown";
    const position = positionMatch?.[1] ?? "unknown";

    return `Your message contains an invisible character (${codePoint} at position ${position}) that was blocked to protect against prompt injection. Please remove it and try again.`;
}

/**
 * Parses protovalidate error format and extracts the message part.
 * Format: "validation error:\n - field_path: message [rule_id]"
 *
 * Note: This uses string parsing because @bufbuild/protovalidate requires
 * @bufbuild/protobuf v2, but the dashboard uses v1. When protobuf is upgraded,
 * this can be replaced with typed ViolationsSchema details.
 */
function parseValidationErrorMessages(rawMessage: string): string[] | null {
    if (!rawMessage.includes("validation error:")) {
        return null;
    }

    const messages: string[] = [];
    const lines = rawMessage.split("\n");

    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("- ")) {
            // Format: "- field_path: message [rule_id]"
            const colonIndex = trimmed.indexOf(": ");
            const bracketIndex = trimmed.lastIndexOf(" [");

            if (colonIndex !== -1 && bracketIndex !== -1 && bracketIndex > colonIndex) {
                messages.push(trimmed.substring(colonIndex + 2, bracketIndex));
            }
        }
    }

    return messages.length > 0 ? messages : null;
}

/**
 * Formats workflow errors for display in toasts and UI.
 * Extracts friendly messages from protovalidate validation errors.
 */
export function formatWorkflowError(error: unknown): string {
    if (error instanceof ConnectError) {
        const messages = parseValidationErrorMessages(error.rawMessage);
        if (messages) {
            return messages.join(". ");
        }
    }

    // Fall back to generic error formatting
    return formatError(error);
}

/**
 * Formats Git authentication error messages for better user experience
 */
export function formatGitAuthError(errorMessage: string): string | null {
    const lowerMessage = errorMessage.toLowerCase();

    // Check for backend error format: "Git authentication failed for {host}: {error}. Please go to Settings > Git authentications..."
    if (
        lowerMessage.includes("git authentication failed for") &&
        lowerMessage.includes("please go to settings > git authentications")
    ) {
        // Extract the host from the error message
        const regex = /Git authentication failed for ([^:]+):/i;
        const hostMatch = regex.exec(errorMessage);
        const host = hostMatch ? hostMatch[1] : "your Git provider";

        return `Authentication with ${host} is required. Please connect your account in Settings > Git authentications and try again.`;
    }

    return null;
}

/**
 * Formats the `error` in a human-readable way
 */
export function formatError(error: unknown, messages?: Partial<Record<Code, string>>): string {
    if (error instanceof ConnectError) {
        // Check for policy-specific errors first
        const policyMessage = formatPolicyError(error);
        if (policyMessage) {
            return policyMessage;
        }

        // Check for prompt validation errors
        const promptValidationMessage = formatPromptValidationError(error.rawMessage);
        if (promptValidationMessage) {
            return promptValidationMessage;
        }

        // Check for Git authentication errors
        const gitAuthMessage = formatGitAuthError(error.rawMessage);
        if (gitAuthMessage) {
            return gitAuthMessage;
        }

        const customMessage = messages?.[error.code];
        if (customMessage) {
            return customMessage;
        }
        return `${codeToText(error.code)}: ${error.rawMessage}`;
    }

    const stripeError = stripeLikeErrorSchema.safeParse(error);
    if (stripeError.success) {
        return stripeError.data.message;
    }

    const stripeLoadError = stripeLikeLoadErrorSchema.safeParse(error);
    if (stripeLoadError.success) {
        return stripeLoadError.data.message;
    }

    if (error instanceof Error) {
        // Check for prompt validation errors in regular Error objects
        const promptValidationMessage = formatPromptValidationError(error.message);
        if (promptValidationMessage) {
            return promptValidationMessage;
        }
        // Check for Git authentication errors in regular Error objects
        const gitAuthMessage = formatGitAuthError(error.message);
        if (gitAuthMessage) {
            return gitAuthMessage;
        }
        return error.message;
    }
    if (typeof error === "string") {
        // Check for prompt validation errors in string errors
        const promptValidationMessage = formatPromptValidationError(error);
        if (promptValidationMessage) {
            return promptValidationMessage;
        }
        // Check for Git authentication errors in string errors
        const gitAuthMessage = formatGitAuthError(error);
        if (gitAuthMessage) {
            return gitAuthMessage;
        }
        return error;
    }
    return "Unknown error";
}

function codeToText(code: Code): string {
    switch (code) {
        case Code.Canceled:
            return "Canceled";
        case Code.InvalidArgument:
            return "Invalid argument";
        case Code.DeadlineExceeded:
            return "Deadline exceeded";
        case Code.NotFound:
            return "Not found";
        case Code.AlreadyExists:
            return "Already exists";
        case Code.PermissionDenied:
            return "Permission denied";
        case Code.ResourceExhausted:
            return "Resource exhausted";
        case Code.FailedPrecondition:
            return "Failed precondition";
        case Code.Aborted:
            return "Aborted";
        case Code.OutOfRange:
            return "Out of range";
        case Code.Unknown:
        case Code.Unimplemented:
        case Code.Internal:
            return "Internal";
        case Code.Unavailable:
            return "Unavailable";
        case Code.DataLoss:
            return "Data loss";
        case Code.Unauthenticated:
            return "Unauthenticated";
    }
}

/**
 * List of all Connect error codes
 * Intended to be used in Storybook and tests.
 */
export const connectErrors = {
    [Code.Canceled]: new ConnectError("message", Code.Canceled),
    [Code.Unknown]: new ConnectError("message", Code.Unknown),
    [Code.InvalidArgument]: new ConnectError("message", Code.InvalidArgument),
    [Code.DeadlineExceeded]: new ConnectError("message", Code.DeadlineExceeded),
    [Code.NotFound]: new ConnectError("message", Code.NotFound),
    [Code.AlreadyExists]: new ConnectError("message", Code.AlreadyExists),
    [Code.PermissionDenied]: new ConnectError("message", Code.PermissionDenied),
    [Code.ResourceExhausted]: new ConnectError("message", Code.ResourceExhausted),
    [Code.FailedPrecondition]: new ConnectError("message", Code.FailedPrecondition),
    [Code.Aborted]: new ConnectError("message", Code.Aborted),
    [Code.OutOfRange]: new ConnectError("message", Code.OutOfRange),
    [Code.Unimplemented]: new ConnectError("message", Code.Unimplemented),
    [Code.Internal]: new ConnectError("message", Code.Internal),
    [Code.Unavailable]: new ConnectError("message", Code.Unavailable),
    [Code.DataLoss]: new ConnectError("message", Code.DataLoss),
    [Code.Unauthenticated]: new ConnectError("message", Code.Unauthenticated),
};

export class FetchError extends Error {
    public readonly status: number;
    public readonly statusText: string;

    constructor(message: string, status: number, statusText: string) {
        super(message);
        this.name = "FetchError";
        this.status = status;
        this.statusText = statusText;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, FetchError);
        }
    }
}

export function isFetchError(error: unknown): error is FetchError {
    return error instanceof FetchError;
}

/**
 * Checks if an error is a policy enforcement error (e.g., per-user environment limits).
 * These errors are terminal — the user cannot recover by retrying or changing options.
 */
export const isPolicyError = (error: unknown): boolean => {
    if (error instanceof ConnectError) {
        const details = error.findDetails(PolicyEnforcedFailedPreconditionDetails);
        return details.length > 0;
    }
    return false;
};

/**
 * Checks if an error is a credits exhaustion error
 * This is used to prevent duplicate error messages when the credits exhaustion modal will handle it
 */
export const isCreditsExhaustionError = (error: unknown): boolean => {
    if (error instanceof ConnectError) {
        return error.message.includes("your organization does not have enough credits");
    }
    return false;
};

/**
 * Checks if an error is a verify card error
 * This is used to prevent duplicate error messages when the credits exhaustion modal will handle it
 */
export const isVerifyCardError = (error: unknown): boolean => {
    if (error instanceof ConnectError) {
        return error.message.includes("your organization has not completed credit card verification");
    }
    return false;
};

/**
 * Formats a payment error for user-facing display. Strips the gRPC status code
 * prefix (e.g. "Failed precondition:") from FailedPrecondition errors so users
 * see only the actionable message from the backend.
 */
export const formatPaymentError = (error: unknown): string => {
    if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
        return error.rawMessage;
    }
    return formatError(error);
};

/**
 * Checks if an error is a missing default payment method error
 * This is used to show the payment method setup modal
 */
export const isNoDefaultPaymentMethodError = (error: unknown): boolean => {
    if (error instanceof ConnectError) {
        return error.code === Code.FailedPrecondition && error.message.includes("no default payment method set up");
    }
    return false;
};

export class MaximumEnvironmentLifetimeExceededError extends Error {
    constructor(
        message: string,
        public readonly maxLifetime: number,
    ) {
        super(message);
        this.name = "MaximumEnvironmentLifetimeExceededError";
    }
}

/**
 * Extracts ActionableErrorDetails from a Connect RPC error and converts it
 * to the same ActionableError shape the ActionableErrorMessage component accepts.
 * Returns null if the error is not a ConnectError or has no ActionableErrorDetails.
 */
export function extractActionableError(error: unknown): ActionableError | null {
    if (!(error instanceof ConnectError)) {
        return null;
    }
    const details = error.findDetails(ActionableErrorDetails);
    if (details.length === 0) {
        return null;
    }
    const detail = details[0];
    return {
        errorCode: detail.errorCode,
        userMessage: detail.userMessage,
        metadata: Object.fromEntries(Object.entries(detail.metadata ?? {})),
    };
}
